import { useState, useRef } from 'react';
import { LEVEL_LABELS } from '../constants';
import Modal from '../ui/Modal';
import { uploadEventImage } from '../lib/supabase';

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  ['00','30'].forEach(m => TIME_OPTIONS.push(`${String(h).padStart(2,'0')}:${m}`));
}
const TIME_END_OPTIONS = [...TIME_OPTIONS,
  '00:00†','00:30†','01:00†','01:30†','02:00†','02:30†','03:00†'
];

function TimeSelect({ value, onChange, options, placeholder }) {
  return (
    <select className="form-select" value={value} onChange={e=>onChange(e.target.value)} style={{colorScheme:"dark"}}>
      <option value="">{placeholder}</option>
      {options.map(t=>{
        const isNext = t.endsWith('†');
        const label = isNext ? t.replace('†','') + ' (+1)' : t;
        return <option key={t} value={t}>{label}</option>;
      })}
    </select>
  );
}

function CityAutocomplete({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const [validated, setValidated] = useState(!!value);
  const timerRef = useRef(null);

  const search = async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowSug(false); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=it`,
        { headers: { 'Accept-Language': 'it' } }
      );
      const data = await res.json();
      const cities = [...new Map(
        data.map(d => d.address?.city || d.address?.town || d.address?.village || d.address?.municipality)
          .filter(Boolean).map(c => [c, c])
      ).values()];
      setSuggestions(cities);
      setShowSug(cities.length > 0);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input className="form-input" placeholder="es. Roma" value={value}
          onChange={e => { setValidated(false); onChange(e.target.value, false); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => search(e.target.value), 400); }}
          onFocus={() => suggestions.length > 0 && setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
          autoComplete="off"
          style={validated ? { borderColor:'var(--success)' } : {}}/>
        {searching && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--muted)' }}>...</span>}
        {validated && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--success)' }}>✓</span>}
      </div>
      {!validated && value.length > 1 && !searching && suggestions.length === 0 && (
        <div style={{ fontSize:16,color:'var(--magenta)',marginTop:3 }}>Scegli una città dall'elenco</div>
      )}
      {showSug && suggestions.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--card-border)',borderRadius:10,marginTop:2,boxShadow:'0 8px 24px rgba(0,0,0,.6)',overflow:'hidden' }}>
          {suggestions.map(c => (
            <div key={c} style={{ padding:'9px 13px',fontSize:16,cursor:'pointer',borderBottom:'1px solid var(--border)' }}
              onMouseDown={() => { setValidated(true); onChange(c, true); setSuggestions([]); setShowSug(false); }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddressAutocomplete({ value, onChange, city }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);
  const hasNumber = (v) => /\d/.test(v);

  const search = async (q) => {
    if (q.length < 3) { setSuggestions([]); setShowSug(false); return; }
    setSearching(true);
    try {
      const query = city ? `${q}, ${city}` : q;
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=it`,
        { headers: { 'Accept-Language': 'it' } }
      );
      const data = await res.json();
      const addresses = [...new Set(data.map(d => {
        const a = d.address;
        const road = a?.road || a?.pedestrian || a?.footway || "";
        const num = a?.house_number ? ` ${a.house_number}` : "";
        return road ? road + num : null;
      }).filter(Boolean))];
      setSuggestions(addresses);
      setShowSug(addresses.length > 0);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input className="form-input" placeholder="es. Via Roma 1" value={value}
          onChange={e => { onChange(e.target.value); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => search(e.target.value), 400); }}
          onFocus={() => suggestions.length > 0 && setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
          autoComplete="off"/>
        {searching && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--muted)' }}>...</span>}
      </div>
      {value.length > 2 && !hasNumber(value) && !searching && (
        <div style={{ fontSize:16,color:'var(--cyan)',marginTop:3 }}>Aggiungi il numero civico per essere più preciso</div>
      )}
      {!value && <div style={{ fontSize:16,color:'var(--muted)',marginTop:3 }}>Opzionale — es. Via dei Giardini 5</div>}
      {showSug && suggestions.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--card-border)',borderRadius:10,marginTop:2,boxShadow:'0 8px 24px rgba(0,0,0,.6)',overflow:'hidden' }}>
          {suggestions.map((a,i) => (
            <div key={i} style={{ padding:'9px 13px',fontSize:16,cursor:'pointer',borderBottom:'1px solid var(--border)' }}
              onMouseDown={() => { onChange(a); setSuggestions([]); setShowSug(false); }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              {a}{!hasNumber(a) && <span style={{fontSize:16,color:'var(--muted)',marginLeft:6}}>+ civico?</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventModal({ onClose, onSave, onDelete, existing, venues, venueEquipment, canCreatePublic = true }) {
  const today = new Date().toISOString().split('T')[0];
  const [confirmDelete1, setConfirmDelete1] = useState(false);
  const [confirmDelete2, setConfirmDelete2] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [title,setTitle]=useState(existing?.title||"");
  const [dateISO,setDateISO]=useState(existing?.date||"");
  const [timeStart,setTimeStart]=useState(existing?.time_start||"");
  const [timeEnd,setTimeEnd]=useState(existing?.time_end||"");
  const [description,setDescription]=useState(existing?.description||"");
  const [venueId,setVenueId]=useState(existing?.venue_id||"");
  // Campi per location libera
  const [locationName,setLocationName]=useState(existing?.location_name||"");
  const [locationAddress,setLocationAddress]=useState(existing?.location||"");
  const [city,setCity]=useState(existing?.city||"");
  const [visibility,setVisibility]=useState(existing?.visibility||(canCreatePublic?"open":"private"));
  const [minLevel,setMinLevel]=useState(existing?.min_level||0);
  const [imageUrl,setImageUrl]=useState(existing?.image_url||"");
  const [imageUploading,setImageUploading]=useState(false);
  const [inviteImageUrl,setInviteImageUrl]=useState(existing?.invite_image_url||"");
  const [inviteUploading,setInviteUploading]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  const selectedVenue=venues.find(v=>v.id===venueId);

  // Quando si seleziona una venue registrata, importa città e address
  const handleVenueChange = (id) => {
    setVenueId(id);
    if (id) {
      const v = venues.find(vn=>vn.id===id);
      if (v) {
        setCity(v.city||"");
        setLocationName("");
        setLocationAddress("");
      }
    }
  };

  const getTimeVal = (t) => {
    if (!t) return null;
    const clean = t.replace('†','');
    const [h,m] = clean.split(':').map(Number);
    return h*60+m + (t.endsWith('†') ? 1440 : 0);
  };
  const timeError = timeStart && timeEnd && getTimeVal(timeEnd) <= getTimeVal(timeStart)
    ? "L'orario di fine deve essere dopo l'inizio" : "";

  const handleSave = async () => {
    setError("");
    if (!title.trim()) { setError("Inserisci un titolo."); return; }
    if (!dateISO) { setError("Inserisci una data."); return; }
    if (!venueId && !city) { setError("Inserisci la città dell'evento."); return; }
    if (!venueId && !locationName.trim()) { setError("Inserisci il nome della location."); return; }
    if (timeError) { setError(timeError); return; }
    setLoading(true);
    try {
      const cleanEnd = timeEnd.replace('†','');
      const eventCity = venueId ? (selectedVenue?.city||"") : city;
      await onSave({
        title, date: dateISO, time_start: timeStart, time_end: cleanEnd,
        city: eventCity,
        location: venueId ? "" : locationAddress,
        location_name: venueId ? "" : locationName,
        description, venue_id: venueId||null, visibility, min_level: minLevel,
        image_url: imageUrl||null,
        // Il campo si manda solo se serve: se supabase/immagine-invito.sql non
        // e' ancora stato lanciato, una colonna sconosciuta farebbe fallire il
        // salvataggio di tutta la serata.
        ...((inviteImageUrl||(existing&&"invite_image_url" in existing))
          ?{invite_image_url: inviteImageUrl||null}:{})
      }, venueId?(venueEquipment[venueId]||[]):[]);
    } catch {
      setError("Errore nel salvataggio. Riprova.");
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
        <div className="modal-title">{existing?"Modifica evento":"Nuovo evento"}</div>
        {error&&<div className="alert alert-error">{error}</div>}

        <div className="form-group"><label className="form-label">Titolo</label>
          <input className="form-input" placeholder="es. Blues Night #3" value={title} onChange={e=>setTitle(e.target.value)}/></div>

        <div className="form-row">
          <div className="form-group"><label className="form-label">Data</label>
            <input className="form-input" type="date" value={dateISO} min={today}
              onChange={e=>setDateISO(e.target.value)} style={{colorScheme:"dark"}}/></div>
          <div className="form-group"><label className="form-label">Orario</label>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <div style={{flex:1}}><TimeSelect value={timeStart} onChange={setTimeStart} options={TIME_OPTIONS} placeholder="Dalle..."/></div>
              <span style={{color:"var(--muted)",fontSize:16}}>→</span>
              <div style={{flex:1}}><TimeSelect value={timeEnd} onChange={setTimeEnd} options={TIME_END_OPTIONS} placeholder="Alle..."/></div>
            </div>
            {timeError&&<div style={{fontSize:16,color:"var(--danger)",marginTop:3}}>{timeError}</div>}
          </div>
        </div>

        {/* Location — due percorsi chiari */}
        <div className="form-group">
          <label className="form-label">Location</label>
          {venues.length>0&&<>
            <select className="form-select" value={venueId} onChange={e=>handleVenueChange(e.target.value)} style={{marginBottom:10}}>
              <option value="">— Nessuna location registrata —</option>
              {venues.map(v=><option key={v.id} value={v.id}>{v.name}{v.city?` · ${v.city}`:""}</option>)}
            </select>
          </>}

          {/* Location registrata: mostra info bloccate */}
          {venueId&&selectedVenue&&(
            <div style={{background:"rgba(237,77,29,.06)",border:"1px solid rgba(237,77,29,.25)",borderRadius:10,padding:"10px 14px",fontSize:16}}>
              <div style={{fontWeight:700,marginBottom:2}}>{selectedVenue.name}</div>
              {selectedVenue.city&&<div style={{color:"var(--muted)",fontSize:16}}>{selectedVenue.city}{selectedVenue.address?` · ${selectedVenue.address}`:""}</div>}
              {(venueEquipment[venueId]||[]).length>0&&<div style={{color:"var(--success)",fontSize:16,marginTop:4}}>✓ Attrezzatura venue importata</div>}
            </div>
          )}

          {/* Location libera: nome + città + indirizzo */}
          {!venueId&&<>
            <div className="form-group" style={{marginBottom:10}}>
              <label className="form-label">Nome location *</label>
              <input className="form-input" placeholder="es. Circolo Magnolia" value={locationName} onChange={e=>setLocationName(e.target.value)}/>
            </div>
            <div className="form-group" style={{marginBottom:10}}>
              <label className="form-label">Città *</label>
              <CityAutocomplete value={city} onChange={(v,valid)=>setCity(v)}/>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Indirizzo</label>
              <AddressAutocomplete value={locationAddress} onChange={setLocationAddress} city={city}/>
            </div>
          </>}
        </div>

        <div className="form-group"><label className="form-label">Visibilità</label>
          <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
            {[["open","Aperta","Chiunque può unirsi"],["request","Su richiesta","L'organizzatore approva"],["private","Privata","Solo su invito"]]
              .filter(([v])=>canCreatePublic||v==="private")
              .map(([v,l,desc])=>(
              <div key={v} onClick={()=>setVisibility(v)}
                style={{flex:1,minWidth:130,padding:"12px 14px",borderRadius:12,cursor:"pointer",
                  border:`2px solid ${visibility===v?"#ED4D1D":"rgba(255,255,255,0.08)"}`,
                  background:visibility===v?"rgba(237,77,29,0.06)":"rgba(255,255,255,0.02)",
                  transition:"all .15s"}}>
                <div style={{fontSize:17,fontWeight:700,color:"var(--text)"}}>{l}</div>
                <div style={{fontSize:16,color:"var(--muted)",marginTop:2}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group"><label className="form-label">Livello minimo</label>
          <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap"}}>
            {LEVEL_LABELS.map((l,i)=><span key={i} className={`chip ${minLevel===i?"selected":""}`} onClick={()=>setMinLevel(i)}>{i===0?"Tutti":l}</span>)}
          </div>
        </div>

        <div className="form-group"><label className="form-label">Locandina / Foto evento</label>
          {imageUrl&&<img src={imageUrl} alt="locandina" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:10,marginBottom:8}}/>}
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:"8px 12px"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span style={{fontSize:16,color:"var(--muted)"}}>{imageUploading?"Caricamento...":imageUrl?"Cambia immagine":"Carica locandina"}</span>
            <input type="file" accept="image/*" style={{display:"none"}} disabled={imageUploading}
              onChange={async(e)=>{
                const file=e.target.files?.[0];
                if(!file)return;
                setImageUploading(true);
                try{
                  const url=await uploadEventImage(file);
                  setImageUrl(url);
                }catch{setError("Errore nel caricamento immagine.");}
                finally{setImageUploading(false);}
              }}/>
          </label>
          {imageUrl&&<button style={{fontSize:16,color:"var(--danger)",background:"none",border:"none",cursor:"pointer",padding:"4px 0"}} onClick={()=>setImageUrl("")}>Rimuovi immagine</button>}
        </div>

        <div className="form-group"><label className="form-label">Immagine per l'invito</label>
          <div style={{fontSize:16,color:"var(--muted)",lineHeight:1.5,marginBottom:8}}>
            Compare nell'anteprima del link Jammo che mandi a chi ha detto sì. Meglio un'immagine semplice e orizzontale, senza scritte: per esempio l'illustrazione della locandina. Se non la metti, si usa la locandina.
          </div>
          {inviteImageUrl&&<img src={inviteImageUrl} alt="immagine per l'invito" style={{width:"100%",aspectRatio:"1.91 / 1",objectFit:"cover",borderRadius:10,marginBottom:8}}/>}
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",minHeight:44,boxSizing:"border-box"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span style={{fontSize:16,color:"var(--muted)"}}>{inviteUploading?"Caricamento...":inviteImageUrl?"Cambia immagine":"Carica immagine per l'invito"}</span>
            <input type="file" accept="image/*" style={{display:"none"}} disabled={inviteUploading}
              onChange={async(e)=>{
                const file=e.target.files?.[0];
                if(!file)return;
                setInviteUploading(true);
                try{
                  const url=await uploadEventImage(file);
                  setInviteImageUrl(url);
                }catch{setError("Errore nel caricamento immagine.");}
                finally{setInviteUploading(false);}
              }}/>
          </label>
          {inviteImageUrl&&<button style={{fontSize:16,color:"var(--danger)",background:"none",border:"none",cursor:"pointer",padding:"8px 0",minHeight:44}} onClick={()=>setInviteImageUrl("")}>Rimuovi immagine</button>}
        </div>

        <div className="form-group"><label className="form-label">Note / Descrizione</label>
          <textarea className="form-textarea" value={description} onChange={e=>setDescription(e.target.value)}/></div>

        {/* Elimina evento — solo in modifica */}
        {existing && onDelete && !confirmDelete1 && !confirmDelete2 && (
          <div style={{marginTop:16,textAlign:'center'}}>
            <button style={{background:'none',border:'none',cursor:'pointer',fontSize:16,
              color:'var(--muted2)',textDecoration:'underline'}}
              onClick={()=>setConfirmDelete1(true)}>
              Elimina questo evento
            </button>
          </div>
        )}
        {confirmDelete1 && (
          <div style={{marginTop:16,padding:'12px 14px',background:'rgba(160,80,80,0.08)',
            border:'1px solid rgba(160,80,80,0.2)',borderRadius:10}}>
            <div style={{fontSize:16,color:'var(--text)',marginBottom:10,lineHeight:1.6}}>
              Verranno eliminati scaletta, partecipanti e chat. Non è reversibile.
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary btn-sm" onClick={()=>setConfirmDelete1(false)}>Annulla</button>
              <button className="btn btn-danger btn-sm" onClick={()=>{setConfirmDelete1(false);setConfirmDelete2(true);}}>Continua</button>
            </div>
          </div>
        )}
        {confirmDelete2 && (
          <div style={{marginTop:16,padding:'12px 14px',background:'rgba(160,80,80,0.12)',
            border:'1px solid rgba(160,80,80,0.35)',borderRadius:10}}>
            <div style={{fontSize:16,fontWeight:700,color:'var(--danger)',marginBottom:10}}>
              Conferma definitiva — stai eliminando "{existing?.title}"
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary btn-sm" onClick={()=>setConfirmDelete2(false)}>No, annulla</button>
              <button className="btn btn-danger btn-sm" disabled={deleting}
                onClick={async()=>{
                  setDeleting(true);
                  try{ await onDelete(existing.id); onClose(); }
                  finally{ setDeleting(false); }
                }}>
                {deleting?<span className="spinner"/>:'Sì, elimina'}
              </button>
            </div>
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" disabled={!title.trim()||!dateISO||!!timeError||loading} onClick={handleSave}>
            {loading?<span className="spinner"/>:existing?"Salva":"Crea evento"}
          </button>
        </div>
    </Modal>
  );
}

export default EventModal;
